<template>
  <app-collapse>
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Goods"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasGoodsData"
            :has-errors="item.issues && item.issues.length > 0"
          />
          Goods
        </span>
      </template>
      <validation-observer ref="observer">
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group>
              <label>
                Goods item sequence number on shipment
                <small class="text-muted">1103000000A</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Goods item sequence number on shipment'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <b-form-input
                v-model="item.goods_data.sequence_on_shipment"
                :disabled="disabled"
              />
            </b-form-group>

            <validation-provider
              v-slot="{ errors }"
              name="Reference number/UCR"
              rules="max:35"
            >
              <b-form-group>
                <label>
                  Reference number/UCR
                  <small class="text-muted">1208000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Unique Consignment Reference'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.goods_data.trader_assigned_reference"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Procedure"
              rules="max:4"
            >
              <b-form-group>
                <label>
                  Procedure <small class="text-muted">1109001000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Requested procedure code'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="item.goods_data.current_procedure"
                  :classifier-key="classifierKeys.procedures"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Previous procedure"
              rules="max:4"
            >
              <b-form-group>
                <label>
                  Previous procedure <small class="text-muted">1109002000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Previous procedure code'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="item.goods_data.previous_procedure"
                  :classifier-key="classifierKeys.procedures"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Additional procedure"
              rules=""
            >
              <b-form-group>
                <label>
                  Additional procedure <small class="text-muted">1110000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Additional procedure codes'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="item.goods_data.additional_procedure"
                  :classifier-key="classifierKeys.additionalProcedures"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Description of goods"
              rules=""
            >
              <b-form-group>
                <label>
                  Description of goods <small class="text-muted">1805000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Detailed description of the goods'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-textarea
                  v-model="item.goods_data.commodity_description"
                  :disabled="disabled"
                  rows="4"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Commodity code"
              rules="integer"
            >
              <b-form-group>
                <label>
                  Commodity code <small class="text-muted">1809000000</small>
                </label>
                <b-form-input
                  id="hs-code"
                  v-model="item.goods_data.commodity_code"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(item), 'hs_code')"
                  :class="issueClass(getUnifiedIssues(item), 'hs_code')"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="CUS code"
              rules="max:10"
            >
              <b-form-group>
                <label>
                  CUS code
                  <small class="text-muted">1808000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Customs Union and Statistics code'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.goods_data.cus_code"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group>
              <label>
                Gross mass
                <small class="text-muted">1804000000</small>
              </label>
              <b-input-group append="kg">
                <b-form-input
                  v-model="item.goods_data.gross_mass"
                  data-cy="h1-item-gross-mass"
                  type="number"
                  step="0.001"
                  :disabled="disabled"
                />
              </b-input-group>
            </b-form-group>

            <validation-provider
              v-slot="{ errors }"
              name="Net mass"
              rules=""
            >
              <b-form-group>
                <label>
                  Net mass <small class="text-muted">1801000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Net weight of the goods in kilograms'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-input-group append="kg">
                  <b-form-input
                    v-model="item.goods_data.net_weight"
                    data-cy="h1-item-net-mass"
                    type="number"
                    step="0.001"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Country of dispatch"
            >
              <b-form-group>
                <label>
                  Country of dispatch
                  <small class="text-muted">1606000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country from which goods are dispatched'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="item.goods_data.export_country"
                  :classifier-key="classifierKeys.countries"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <b-form-group>
              <label>
                Country of destination
                <small class="text-muted">1603000000</small>
              </label>
              <classifier-select
                v-model="item.goods_data.destination_country"
                :classifier-key="classifierKeys.countries"
                :disabled="disabled"
              />
            </b-form-group>

            <b-form-group>
              <label>
                Region of destination
                <small class="text-muted">1604000000</small>
              </label>
              <b-form-input
                v-model="item.goods_data.destination_region"
                :disabled="disabled"
              />
            </b-form-group>

            <validation-provider
              v-slot="{ errors }"
              name="Country of origin"
              rules=""
            >
              <b-form-group>
                <label>
                  Country of origin <small class="text-muted">1608000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country where goods were produced or manufactured'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="item.goods_data.origin_country"
                  :classifier-key="classifierKeys.countries"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Country of preferential origin"
            >
              <b-form-group>
                <label>
                  Country of preferential origin
                  <small class="text-muted">1609000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Country conferring preferential origin'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <classifier-select
                  v-model="item.goods_data.preferential_origin_country"
                  :classifier-key="classifierKeys.countries"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Duty regime preference code"
              :rules="{ regex: /^[0-9]{1,3}$/ }"
            >
              <b-form-group>
                <label>
                  Duty regime preference code
                  <small class="text-muted">1411000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Duty regime preference code'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.goods_data.duty_regime"
                  :disabled="disabled"
                  maxlength="3"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="Quota order number"
            >
              <b-form-group>
                <label>
                  Quota order number
                  <small class="text-muted">9901000000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Tariff quota order number'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.goods_data.quota_order"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="TARIC additional code"
            >
              <b-form-group>
                <label>
                  TARIC additional code
                  <small class="text-muted">1809059000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'TARIC additional code for specific measures'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.goods_data.taric_additional"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              name="National additional code"
            >
              <b-form-group>
                <label>
                  National additional code
                  <small class="text-muted">1809060000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'National additional code for specific measures'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="item.goods_data.national_additional"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BInputGroup,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS } from '@/libs/customsClassifiers'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    ValidationObserver,
    ValidationProvider,
    ClassifierSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { classifierKeys: CUSTOMS_CLASSIFIER_KEYS }
  },
  computed: {
    hasGoodsData() {
      if (!this.item || !this.item.goods_data) {
        return false
      }
      const {
        trader_assigned_reference: traderAssignedReference,
        current_procedure: currentProcedure,
        previous_procedure: previousProcedure,
        additional_procedure: additionalProcedure,
        commodity_description: commodityDescription,
        commodity_code: commodityCode,
        cus_code: cusCode,
        export_country: exportCountry,
        destination_country: destinationCountry,
        destination_region: destinationRegion,
        origin_country: originCountry,
        preferential_origin_country: preferentialOriginCountry,
        duty_regime: dutyRegime,
        quota_order: quotaOrder,
        taric_additional: taricAdditional,
        national_additional: nationalAdditional,
      } = this.item.goods_data
      return !!(
        traderAssignedReference
        || currentProcedure
        || previousProcedure
        || additionalProcedure
        || commodityDescription
        || commodityCode
        || cusCode
        || this.item.gross_weight
        || this.item.net_weight
        || exportCountry
        || destinationCountry
        || destinationRegion
        || originCountry
        || preferentialOriginCountry
        || dutyRegime
        || quotaOrder
        || taricAdditional
        || nationalAdditional
        // Also check item.description and item.hs_code directly (before they're synced to goods_data)
        || this.item.description
        || this.item.hs_code
      )
    },
  },
  mounted() {
    // Initialize commodity_description from item.description when component mounts
    if (this.item && this.item.description != null && this.item.goods_data) {
      if (this.item.goods_data.commodity_description == null) {
        this.$set(this.item.goods_data, 'commodity_description', this.item.description)
      }
    }
    // Initialize commodity_code from item.hs_code when component mounts
    if (this.item && this.item.hs_code != null && this.item.goods_data) {
      if (this.item.goods_data.commodity_code == null) {
        this.$set(this.item.goods_data, 'commodity_code', this.item.hs_code)
      }
    }
    // Initialize origin_country from item.origin_country when component mounts
    if (this.item && this.item.origin_country != null && this.item.goods_data) {
      if (this.item.goods_data.origin_country == null) {
        this.$set(this.item.goods_data, 'origin_country', this.item.origin_country)
      }
    }
  },
  methods: {
    getUnifiedIssues(item) {
      if (item.issues && item.issues.length > 0) {
        return item.issues
      }
      return []
    },
    issueDescription(issues, field) {
      if (issues?.length) {
        const index = issues.findIndex(item => item.field === field)

        if (index !== -1) {
          return issues[index].message
        }

        return ''
      }
      return ''
    },
    issueClass(issues, field) {
      if (issues?.length) {
        const index = issues.findIndex(item => item.field === field)

        if (index !== -1) {
          return issues[index].type === 'warning' ? 'input-warning' : 'input-danger'
        }

        return ''
      }
      return ''
    },
    async validate() {
      return this.$refs.observer.validate()
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}
</style>
